import { ShelfPack } from "./ShelfPack";
/**
 * FontAtlasPacker is a higher level wrapper over ShelfPack that aggregates over multiple atlas textures. Will
 * dynamically create new atlas textures whenever the current atlas fills.
 */
export class FontAtlasPacker {
    /**
     * Constructs a new FontAtlasPacker that will manage packing rectangles into multiple atlas textures.
     *
     * All atlas textures will be the same size (width x height).
     *
     * @param width The width each atlas texture will be
     * @param height The height each atlas texture will be
     */
    constructor(width, height) {
        this.pages = [];
        this._width = width;
        this._height = height;
    }
    /**
     * Takes an array of rectangles and packs them into the underlying shelves. There is no guarantee all requested
     * rectangles will be packed into the same atlas textures. Make sure to check 'atlasIndex' for each output rectangle
     * to know which atlas the rectangle was packed into.
     *
     * @param bins Array of rectangles to pack
     */
    pack(bins) {
        if (this.pages.length === 0) {
            this.pages.push(new ShelfPack(this._width, this._height));
        }
        bins = [...bins];
        // Iterate over all our input bins and try and pack them into the set of pages one by one
        const results = [];
        for (const item of bins) {
            // First we try and find space in one of the existing pages.
            //
            // Iterate over each existing page and keep trying until we successfully allocate into one of them. We
            // iterate in reverse so we always try the most recently added page to avoid skipping over all the full
            // pages that are unlikely to have space for the item. We only check the old pages as a last resort to
            // creating a new atlas page.
            let allocated = false;
            for (let i = this.pages.length - 1; i >= 0; i += 1) {
                const page = this.pages[i];
                const allocation = page.packOne(item.w, item.h, item.id);
                if (allocation) {
                    results.push(Object.assign({ atlasIndex: i }, allocation));
                    allocated = true;
                    break;
                }
            }
            // If we failed to find a slot in any of the existing pages then we need to create a new page. This can
            // still technically fail, but only in cases where the requested item to pack is larger than the size of the
            // backing pages.
            if (!allocated) {
                const page = new ShelfPack(this._width, this._height);
                const allocation = page.packOne(item.w, item.h, item.id);
                if (allocation) {
                    results.push(Object.assign({ atlasIndex: this.pages.length }, allocation));
                    this.pages.push(page);
                }
                else {
                    throw new Error("Rectangle too large to allocate");
                }
            }
        }
        return results;
    }
    get numPages() {
        return this.pages.length;
    }
    get width() {
        return this._width;
    }
    get height() {
        return this._height;
    }
}
